// src/components/Dashboard.js
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Outlet, NavLink } from 'react-router-dom';
import TopBar from './ui/TopBar';
import Drawer from './ui/Drawer';
import socket from '../socket';
import useSocketEvent from '../utils/useSocketEvent';
import { loadActiveShow, loadPlaysets, setActiveShow } from '../integrations/quizApiAdapter';

const navLinks = [
    { to: '/dashboard/shows', label: 'Shows' },
    { to: '/dashboard/home', label: 'Moderation' },
    { to: '/dashboard/matrix', label: 'Matrix' },
    { to: '/dashboard/finale-decoder', label: 'Finale' },
    { to: '/dashboard/fragen', label: 'Fragenkatalog' },
    { to: '/dashboard/spielermanagement', label: 'Spielermanagement' },
    { to: '/dashboard/optionen', label: 'Optionen' },
];

function Dashboard() {
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [navCountdown, setNavCountdown] = useState(null);
    const [predictionCountdown, setPredictionCountdown] = useState(null);
    const [shows, setShows] = useState([]);
    const [activePlaysetId, setActivePlaysetId] = useState(null);

    const handleCountdown = useCallback(({ timeLeft }) => setNavCountdown(timeLeft), []);
    const handleCountdownEnded = useCallback(() => setNavCountdown(null), []);

    useSocketEvent(socket, 'countdownStarted', handleCountdown);
    useSocketEvent(socket, 'countdownTick', handleCountdown);
    useSocketEvent(socket, 'countdownPaused', handleCountdown);
    useSocketEvent(socket, 'countdownEnded', handleCountdownEnded);
    useSocketEvent(socket, 'predictionStarted', ({ timeLeft }) => setPredictionCountdown(timeLeft));
    useSocketEvent(socket, 'predictionTick', ({ timeLeft }) => setPredictionCountdown(timeLeft));
    useSocketEvent(socket, 'predictionEnded', () => setPredictionCountdown(null));

    const fetchShowsAndActive = useCallback(async () => {
        try {
            const [showsData, active] = await Promise.all([loadPlaysets(), loadActiveShow()]);
            setShows(Array.isArray(showsData) ? showsData : []);
            setActivePlaysetId(active?.playsetId ?? null);
        } catch (err) {
            console.warn('[Dashboard] Konnte Shows/aktive Show nicht laden:', err);
        }
    }, []);

    useEffect(() => {
        fetchShowsAndActive();
    }, [fetchShowsAndActive]);

    const handleActiveShowUpdate = useCallback((payload) => {
        setActivePlaysetId(payload?.playsetId ?? null);
    }, []);
    useSocketEvent(socket, 'show:active', handleActiveShowUpdate);

    // Show-Liste aktualisieren, wenn sich auf der Shows-Seite was geändert hat
    const handleShowsChanged = useCallback(() => {
        loadPlaysets()
            .then((data) => setShows(Array.isArray(data) ? data : []))
            .catch(() => {});
    }, []);
    useSocketEvent(socket, 'shows:changed', handleShowsChanged);

    const handleSwitchShow = useCallback(async (event) => {
        const newId = event.target.value;
        if (!newId) return;
        try {
            const res = await setActiveShow(newId);
            setActivePlaysetId(res?.playsetId ?? newId);
        } catch (err) {
            console.error('[Dashboard] Show-Wechsel fehlgeschlagen:', err);
        }
    }, []);

    const activeShows = useMemo(
        () => shows.filter((s) => s.status !== 'archived'),
        [shows]
    );

    const showSwitcher = (
        <label className="topbar-show-switcher">
            <span style={{ fontSize: '0.85em', opacity: 0.85 }}>Show:</span>
            <select
                value={activePlaysetId ?? ''}
                onChange={handleSwitchShow}
                aria-label="Aktive Show wechseln"
            >
                {!activePlaysetId && <option value="">— keine —</option>}
                {activeShows.map((s) => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                ))}
            </select>
        </label>
    );

    const closeDrawer = () => setDrawerOpen(false);

    const renderNavLinks = (onClick) => (
        <ul className="drawer-nav-links">
            {navLinks.map((link) => (
                <li key={link.to}>
                    <NavLink
                        to={link.to}
                        className={({ isActive }) => isActive ? 'active' : ''}
                        onClick={onClick}
                    >
                        {link.label}
                    </NavLink>
                </li>
            ))}
        </ul>
    );

    return (
        <div>
            <TopBar
                title="Besserwisser ?"
                onMenuClick={() => setDrawerOpen(true)}
                navLinks={navLinks}
                countdownValue={predictionCountdown ?? navCountdown}
                countdownVariant={predictionCountdown != null ? 'prediction' : 'countdown'}
                showSwitcher={showSwitcher}
            />

            <div className="dashboard-layout">
                <main className="dashboard-content">
                    <Outlet />
                </main>
            </div>

            <Drawer open={drawerOpen} onClose={closeDrawer}>
                <nav aria-label="Navigation">
                    {renderNavLinks(closeDrawer)}
                </nav>
            </Drawer>
        </div>
    );
}

export default Dashboard;
